import React from 'react'
import { Form, Rate, Input, Checkbox, Button, Upload } from 'antd'
import type { FormInstance } from 'antd/es/form'
import type { UploadFile } from 'antd/es/upload/interface'
import { StarFilled, PlusOutlined, LoadingOutlined } from '@ant-design/icons'
import './googleStyleReviewForm.css'

const { TextArea } = Input

export interface GoogleStyleReviewFormProps {
  form: FormInstance
  businessName: string
  businessLogo: string | null
  customerFirstName: string | null
  submitting: boolean
  uploadingImage: boolean
  fileList: UploadFile[]
  onFinish: (values: Record<string, unknown>) => void
  onImageUpload: (file: File) => void
  onImageRemove: () => void
}

export const GoogleStyleReviewForm: React.FC<GoogleStyleReviewFormProps> = ({
  form,
  businessName,
  businessLogo,
  customerFirstName,
  submitting,
  uploadingImage,
  fileList,
  onFinish,
  onImageUpload,
  onImageRemove,
}) => {
  const avatarInitial = businessName.trim().charAt(0).toUpperCase() || '?'

  return (
    <div className="google-review-page">
      <div className="google-review-container">
        <div className="google-review-card">
          <div className="google-review-header">
            {businessLogo ? (
              <img
                src={businessLogo}
                alt={businessName}
                className="google-review-avatar"
              />
            ) : (
              <div className="google-review-avatar-placeholder" aria-hidden>
                {avatarInitial}
              </div>
            )}
            <h1 className="google-review-business-name">{businessName}</h1>
          </div>

          <p className="google-review-subtitle">
            We&apos;re sorry to hear that. Please share details of your experience
            {customerFirstName ? `, ${customerFirstName}` : ''} so we can improve.
          </p>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="google-review-form"
            initialValues={{
              reviewerName: customerFirstName || '',
            }}
          >
            <Form.Item
              name="rating"
              className="google-review-rating-item"
              rules={[{ required: true, message: 'Please select a rating' }]}
            >
              <Rate
                className="google-review-stars"
                character={<StarFilled />}
              />
            </Form.Item>

            <Form.Item
              name="feedback"
              className="google-review-main-textarea"
              rules={[{ required: true, message: 'Please share details of your experience' }]}
            >
              <TextArea
                rows={4}
                placeholder="Share details of your own experience at this place"
                maxLength={1000}
                showCount
              />
            </Form.Item>

            <div className="google-review-secondary-section">
              <div className="google-review-secondary-section-title">
                Optional
              </div>

              <Form.Item
                name="publicReview"
                label="Public review"
                className="google-review-secondary-field"
              >
                <TextArea
                  rows={3}
                  placeholder="This may be displayed on our website if approved..."
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item
                name="reviewerName"
                label="Your name"
                className="google-review-secondary-field"
              >
                <Input placeholder="e.g., John S." />
              </Form.Item>

              <Form.Item label="Photo" className="google-review-secondary-field">
                <Upload
                  listType="picture-card"
                  fileList={fileList}
                  beforeUpload={(file) => {
                    onImageUpload(file as File)
                    return false
                  }}
                  onRemove={onImageRemove}
                  maxCount={1}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                >
                  {fileList.length >= 1 ? null : (
                    <div>
                      {uploadingImage ? <LoadingOutlined /> : <PlusOutlined />}
                      <div style={{ marginTop: 8 }}>Add photos</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>

              <Form.Item name="isPublic" valuePropName="checked" className="google-review-consent">
                <Checkbox>
                  I agree to have my review displayed publicly (if approved)
                </Checkbox>
              </Form.Item>
            </div>

            <Form.Item className="google-review-submit">
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                className="google-review-submit-btn"
                block
              >
                Post
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  )
}
